import PropTypes from 'prop-types';

// PropTypes mirror of the DriverRecord typedef in useDriverLocationsPoll.js —
// keep the two in sync when the composer's output shape changes.

export const routeStopShape = PropTypes.shape({
  stopIndex: PropTypes.number,
  stopId: PropTypes.string,
  stopName: PropTypes.string,
  latitude: PropTypes.number,
  longitude: PropTypes.number,
  type: PropTypes.string,
  state: PropTypes.oneOf(['done', 'current', 'pending']),
  plannedWindow: PropTypes.string,
  plannedMin: PropTypes.number,
  plannedStartMs: PropTypes.number,
  plannedEndMs: PropTypes.number,
  stockedAt: PropTypes.string,
  stockedAtMs: PropTypes.number,
  outOfSequence: PropTypes.bool,
  skipped: PropTypes.bool,
  scannedBy: PropTypes.string,
  visitOrder: PropTypes.number,
  scannedAt: PropTypes.string,
  scannedAtMs: PropTypes.number,
  actualDurationMin: PropTypes.number,
  etaLabel: PropTypes.string,
  delta: PropTypes.number,
  // Traffic-aware drive-leg fields — present once the inbound leg is reported.
  driveDurationMin: PropTypes.number,
  staticDurationMin: PropTypes.number,
  trafficDelayMin: PropTypes.number,
  distanceMiles: PropTypes.number,
  legDepartedAtMs: PropTypes.number,
  legEtaMs: PropTypes.number,
  legEtaLabel: PropTypes.string,
  legOrigin: PropTypes.string,
  legPolyline: PropTypes.string,
  // Forecast fields — present on pending stops of active routes only.
  originalEtaMs: PropTypes.number,
  currentEtaMs: PropTypes.number,
  originalEtaLabel: PropTypes.string,
  currentEtaLabel: PropTypes.string,
  riskTier: PropTypes.oneOf(['red', 'yellow', 'green']),
  riskReason: PropTypes.string,
});

export const timelineRowShape = PropTypes.shape({
  state: PropTypes.oneOf(['done', 'current', 'pending']),
  positionLabel: PropTypes.string,
  stopName: PropTypes.string,
  stopCode: PropTypes.string,
  timeLabel: PropTypes.string,
  durationLabel: PropTypes.string,
  deltaLabel: PropTypes.string,
  deltaTier: PropTypes.oneOf(['early', 'on-time', 'over', 'late', 'eta']),
});

// One recorded GPS reading on a driver's trail — a position, when it was
// taken, who reported it, and the phone's battery at that moment.
const trailPointShape = PropTypes.shape({
  driverEmail: PropTypes.string,
  route: PropTypes.string,
  latitude: PropTypes.number,
  longitude: PropTypes.number,
  timestampMs: PropTypes.number,
  driverId: PropTypes.string,
  batteryLevel: PropTypes.number,
});

export const driverRecordShape = PropTypes.shape({
  driverId: PropTypes.string.isRequired,
  driverEmail: PropTypes.string,
  driverName: PropTypes.string,
  // Everyone with evidence of working the route (see composeDriverRecords),
  // as display names and as the same roster keyed on email.
  driverNames: PropTypes.arrayOf(PropTypes.string),
  driverRoster: PropTypes.arrayOf(
    PropTypes.shape({
      email: PropTypes.string,
      name: PropTypes.string,
    }),
  ),
  // The stop the driver is driving to (newest in-flight leg), or null.
  nextStopId: PropTypes.string,
  routeId: PropTypes.string.isRequired,
  routeName: PropTypes.string,
  status: PropTypes.oneOf([
    'on-track',
    'behind',
    'off-track',
    'not-started',
    'completed',
  ]).isRequired,
  etaDeltaMin: PropTypes.number,
  stopsCompleted: PropTypes.number,
  stopsTotal: PropTypes.number,
  latitude: PropTypes.number,
  longitude: PropTypes.number,
  batteryLevel: PropTypes.number,
  timestamp: PropTypes.string,
  lastPingSecondsAgo: PropTypes.number,
  timeline: PropTypes.arrayOf(timelineRowShape),
  fullTimeline: PropTypes.arrayOf(timelineRowShape),
  remainingStopsCount: PropTypes.number,
  routeStops: PropTypes.arrayOf(routeStopShape),
  // Recorded GPS positions for the day, oldest first — the driven path the
  // single-route map draws.
  pingTrail: PropTypes.arrayOf(trailPointShape),
  trafficDelayTodayMin: PropTypes.number,
  plannedDepartureLabel: PropTypes.string,
  backAtDepotLabel: PropTypes.string,
  // Breakdown scaffolding — never produced by the composer today (see the
  // scaffolding note in MonitoringTab.js).
  vehicleName: PropTypes.string,
  breakdownReported: PropTypes.bool,
});
